import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import PasswordInput from '@/components/PasswordInput';
import { Input } from '@/components/ui/input';
import { Mail, Lock, Chrome, ArrowRight, ArrowLeft, Sun, Moon, Shield, Loader2, Sparkles, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { API_URL as API } from '@/utils/apiConfig';

// Only allow same-origin, path-only redirects (no protocol, no host, no
// protocol-relative "//evil.com" URLs) — this prevents an open-redirect
// vulnerability via ?returnTo=.
function safeReturnTo(raw) {
  if (!raw) return '/app';
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return '/app';
    return decoded;
  } catch {
    return '/app';
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const { t } = useTranslation();
  const { colorMode, changeColorMode } = useTheme();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [lockoutInfo, setLockoutInfo] = useState(null);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(true);

  // Check if Google login is enabled
  useEffect(() => {
    const checkGoogleLogin = async () => {
      try {
        const res = await axios.get(`${API}/auth/google-login-enabled`);
        setGoogleLoginEnabled(res.data.enabled);
      } catch {
        // Default to enabled if check fails
        setGoogleLoginEnabled(true);
      }
    };
    checkGoogleLogin();

    // Purge any home-page stale-while-revalidate cache from a previous
    // session so a new user never sees the prior user's reflection /
    // unread count / active study plans during the brief moment between
    // login and the first fresh fetch. Cache lives in localStorage (so
    // it survives app close/reopen — that's the whole point), which
    // means we must explicitly purge on the login screen.
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('home_cache_') || k.startsWith('eili_cache_v1:')) {
          localStorage.removeItem(k);
        }
      });
      // Also purge any stale sessionStorage entries left from previous
      // app versions (pre-localStorage migration) — cheap safety net.
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith('home_cache_')) sessionStorage.removeItem(k);
      });
    } catch { /* private mode */ }
  }, []);

  // Auth redirect is handled by AuthGate in App.js — no check needed here

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('Eili')}`;
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/guest`, {}, { withCredentials: true });
      // Use replace to prevent back button issues
      window.location.replace(returnTo);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create guest session';
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('login.failed_guest'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { identifier, password };
      if (needs2FA && totpCode) payload.totp_code = totpCode;
      const res = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });

      if (res.data.requires_2fa) {
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      // Use replace to prevent back button issues
      window.location.replace(returnTo);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        if (detail.error === 'account_locked') {
          setLockoutInfo({ type: 'permanent', message: detail.message });
        } else if (detail.error === 'account_temp_locked') {
          setLockoutInfo({
            type: 'temporary',
            message: detail.message,
            retryMinutes: detail.retry_minutes,
            lockedUntil: detail.locked_until,
          });
        } else if (detail.error === 'invalid_credentials') {
          setLockoutInfo(null);
          toast.error(detail.message);
        } else {
          toast.error(detail.message || 'Login failed');
        }
      } else {
        const errorMessage = typeof detail === 'string' ? detail : (error.message || 'Login failed');
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 py-10" style={{ minHeight: '100dvh', paddingTop: '2.5rem', background: 'var(--theme-background)' }}>
      {/* Back to landing — top left. Signed-out visitors (or a user
          who just logged out) always have a one-tap way back to the
          marketing / onboarding surface. Hidden inside the Despia
          native wrapper, since native users' equivalent "home" is
          the onboarding carousel, not the marketing landing. */}
      {typeof navigator !== 'undefined' && !navigator.userAgent.toLowerCase().includes('despia') && (
        <div className="fixed top-4 left-4 z-50" style={{ top: 'max(1rem, var(--safe-area-top, env(safe-area-inset-top, 0px)))' }}>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-all hover:scale-[1.03] active:scale-95"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-textSecondary)', backdropFilter: 'blur(12px)' }}
            data-testid="login-back-to-home"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('login.back_to_home', 'Back to home')}</span>
          </button>
        </div>
      )}
      {/* Mode toggle — top right */}
      <div className="fixed top-4 right-4 z-50" style={{ top: 'max(1rem, var(--safe-area-top, env(safe-area-inset-top, 0px)))' }}>
        <button
          onClick={() => changeColorMode(colorMode === 'dark' ? 'light' : 'dark')}
          className="p-2 rounded-full transition-all"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          data-testid="login-mode-toggle"
        >
          {colorMode === 'dark'
            ? <Sun className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            : <Moon className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />}
        </button>
      </div>
      {/* Glass card */}
      <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', boxShadow: '0 32px 64px rgba(0,0,0,0.15)' }}>
        {/* Logo + title */}
        <div className="text-center pt-10 pb-6 px-8">
          <button onClick={() => navigate('/app')} className="inline-block mb-5 hover:scale-105 transition-transform">
            <img src="/logo.png" alt={t('generated.loginpage_eili_a51f')} className="h-20 w-20 mx-auto drop-shadow-lg" data-testid="login-logo" />
          </button>
          <h1 className="text-3xl font-bold mb-1.5" style={{ color: 'var(--theme-text)', fontFamily: "'Playwrite ES Deco', cursive" }}>{t('app.title')}</h1>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('signup.desc')}</p>
        </div>

        {/* Form area */}
        <div className="px-8 pb-10 space-y-4">
          {!showEmailLogin ? (
            <>
              {googleLoginEnabled && (
                <>
                  <Button
                    onClick={handleGoogleLogin}
                    className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-3 transition-all"
                    style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                    data-testid="google-login-button"
                  >
                    <Chrome className="h-5 w-5" />
                    {t('login.google')}
                  </Button>

                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full" style={{ borderTop: '1px solid var(--glass-border)' }} />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-4 uppercase tracking-widest" style={{ color: 'var(--theme-textSecondary)', background: 'var(--theme-background)' }}>{t('login.or')}</span>
                    </div>
                  </div>
                </>
              )}

              <Button
                onClick={() => setShowEmailLogin(true)}
                className="w-full py-6 rounded-xl text-sm font-medium transition-all"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
              >
                {t('login.email')}
              </Button>

              <div className="text-center pt-3">
                <button onClick={() => navigate(`/signup${returnTo !== '/app' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`)} className="text-sm transition-colors" style={{ color: 'var(--theme-textSecondary)' }}>
                  {t('login.no_account')} <span className="underline" style={{ color: 'var(--theme-accent)' }}>{t('login.signup')}</span>
                </button>
              </div>

              <div className="pt-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
                <button
                  onClick={handleGuestLogin}
                  disabled={loading}
                  className="w-full py-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="guest-login-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />}
                  {t('login.guest', 'Continue as Guest')}
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                <Input
                  type="text"
                  placeholder={t('login.identifier_placeholder', 'Email or username')}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  className="pl-12 py-6 rounded-xl text-sm"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="login-email-input"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" style={{ color: 'var(--theme-textSecondary)' }} />
                <PasswordInput
                  InputComponent={Input}
                  placeholder={t('login.password_placeholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-12 py-6 rounded-xl text-sm"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="login-password-input"
                />
              </div>

              {needs2FA && (
                <div className="relative" data-testid="login-2fa-section">
                  <Shield className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={t('generated.loginpage_authenticator_code_3dd4')}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    required
                    className="pl-12 py-6 rounded-xl text-sm font-mono tracking-widest"
                    style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--theme-accent)', color: 'var(--theme-text)' }}
                    data-testid="login-totp-input"
                  />
                </div>
              )}

              {lockoutInfo && <LockoutBanner info={lockoutInfo} onForgotPassword={() => navigate('/forgot-password')} />}

              <Button
                type="submit"
                disabled={loading || (needs2FA && totpCode.length !== 6)}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2 text-white transition-all hover:opacity-90"
                style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : needs2FA ? <Shield className="h-4 w-4" /> : null}
                {needs2FA ? 'Verify & Sign In' : t('login.signin')}
                {!needs2FA && <ArrowRight className="h-4 w-4" />}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm transition-colors"
                  style={{ color: 'var(--theme-textSecondary)' }}
                  data-testid="forgot-password-link"
                >
                  {t('login.forgot_password', 'Forgot your password?')}
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowEmailLogin(false)}
                className="w-full py-3 text-sm transition-colors"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                {t('login.back_to_options')}
              </button>
            </form>
          )}
        </div>
        {/* Legal links */}
        <div className="text-center mt-4 flex items-center justify-center gap-3">
          <a
            href="/terms"
            className="text-xs transition-colors hover:underline"
            style={{ color: 'var(--theme-textSecondary)' }}
            data-testid="login-terms-link"
          >
            {t('generated.loginpage_terms_of_use_ec6b')}
          </a>
          <span className="text-xs" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>&middot;</span>
          <a
            href="/privacy"
            className="text-xs transition-colors hover:underline"
            style={{ color: 'var(--theme-textSecondary)' }}
            data-testid="login-privacy-link"
          >
            {t('generated.loginpage_privacy_policy_a565')}
          </a>
        </div>
      </div>
    </div>
  );
}

function LockoutBanner({ info, onForgotPassword }) {
  const {
    t
  } = useTranslation();

  const [remaining, setRemaining] = useState(info.retryMinutes || 0);

  useEffect(() => {
    if (info.type !== 'temporary' || !info.lockedUntil) return;
    const update = () => {
      const diff = Math.max(0, Math.ceil((new Date(info.lockedUntil) - Date.now()) / 60000));
      setRemaining(diff);
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [info]);

  if (info.type === 'permanent') {
    return (
      <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="lockout-permanent-banner">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="text-sm font-medium text-red-500">{t('generated.loginpage_account_locked_e524')}</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
          {t('generated.loginpage_your_account_has_c03f')}{' '}
          <a href="mailto:support@askeili.com" className="underline text-red-400 font-medium">{t('generated.loginpage_support_askeili_com_dc5f')}</a>
          {' '}{t('generated.loginpage_to_unlock_your_b2ec')}
        </p>
        <button onClick={onForgotPassword} className="text-xs underline text-blue-400 mt-1" data-testid="lockout-forgot-password">
          {t('generated.loginpage_or_reset_your_05f9')}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }} data-testid="lockout-temp-banner">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
        <span className="text-sm font-medium text-amber-500">{t('generated.loginpage_temporarily_locked_0bc2')}</span>
      </div>
      <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
        {t('generated.loginpage_too_many_failed_a2bd')} <span className="font-semibold text-amber-400">{remaining} minute(s)</span>.
              </p>
      <button onClick={onForgotPassword} className="text-xs underline text-blue-400 mt-1" data-testid="lockout-forgot-password">
        {t('generated.loginpage_reset_your_password_870f')}
      </button>
    </div>
  );
}
